import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Loader2, Sparkles } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_AMOUNTS = [5, 10, 25, 50, 100];
const ROLLOVER_OPTIONS = [
    { value: 0, label: 'No rollover', tag: 'Instant cashout' },
    { value: 1, label: '1× rollover', tag: 'Wager amount once' },
    { value: 2, label: '2× rollover', tag: 'Wager amount twice' },
    { value: 3, label: '3× rollover', tag: 'Wager amount 3×' },
    { value: 5, label: '5× rollover', tag: 'Wager amount 5×' },
];
const DEFAULT_REASON = "Free play on the house — enjoy!";

const FreePlayQuickDialog = ({ user, onGranted, trigger }) => {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState(DEFAULT_REASON);
    const [rollover, setRollover] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setAmount('');
        setReason(DEFAULT_REASON);
        setRollover(0);
    };

    const rollRandom = () => {
        // Random integer between $1 and $25 — friendly, suspenseful "lucky drop"
        const r = Math.floor(Math.random() * 25) + 1;
        setAmount(String(r));
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const parsed = parseFloat(amount);
        if (!parsed || parsed <= 0) {
            toast.error('Enter an amount greater than 0');
            return;
        }
        if (parsed > 1000) {
            toast.error('Free play amount must be ≤ $1,000');
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/admin/promotions/give`,
                {
                    user_ids: [user.id],
                    amount: parsed,
                    reason: (reason || '').trim() || DEFAULT_REASON,
                    rollover_multiplier: rollover,
                },
                { withCredentials: true }
            );
            const rolloverSuffix = rollover > 0
                ? ` (${rollover}× rollover • $${(parsed * rollover).toFixed(2)} to wager)`
                : '';
            toast.success(`+$${parsed.toFixed(2)} free play sent to ${user.name}${rolloverSuffix}`);
            setOpen(false);
            reset();
            if (onGranted) onGranted();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send free play');
        }
        setSubmitting(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <Gift className="w-5 h-5 text-[#FF007F]" /> Free Play — {user.name}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Drop a custom free-play amount on this player. Lands in their balance instantly + a "Free Play!" notification.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="freeplay-amount" className="text-white">Amount ($)</Label>
                        <div className="flex gap-2">
                            <Input
                                id="freeplay-amount"
                                type="number"
                                step="0.01"
                                min="0.01"
                                max="1000"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="bg-[#0a0a0a] border-white/10 text-white text-lg font-mono flex-1"
                                data-testid="freeplay-amount-input"
                                autoFocus
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={rollRandom}
                                className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 shrink-0"
                                data-testid="freeplay-random-btn"
                                title="Random $1–$25"
                            >
                                <Sparkles className="w-4 h-4 mr-1" /> Random
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {QUICK_AMOUNTS.map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => setAmount(String(q))}
                                    className="px-3 py-1 rounded-md text-xs font-bold bg-[#0a0a0a] border border-white/10 text-[#A0AAB2] hover:border-[#FF007F]/40 hover:text-[#FF007F] transition"
                                    data-testid={`freeplay-quick-${q}`}
                                >
                                    ${q}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="freeplay-reason" className="text-white">Reason / Note <span className="text-[#666] font-normal">(shown to player)</span></Label>
                        <Input
                            id="freeplay-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            maxLength={140}
                            className="bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="freeplay-reason-input"
                        />
                    </div>

                    {/* Wagering requirement (rollover) */}
                    <div className="space-y-2">
                        <Label className="text-white flex items-center gap-1.5">
                            Wagering requirement
                            <span className="text-[10px] font-normal text-[#666]">— blocks cashout until satisfied</span>
                        </Label>
                        <div className="grid grid-cols-5 gap-1.5">
                            {ROLLOVER_OPTIONS.map((opt) => {
                                const active = rollover === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setRollover(opt.value)}
                                        className={`py-1.5 px-1 rounded-md text-[11px] font-bold transition border ${
                                            active
                                                ? 'bg-[#FFD700]/15 border-[#FFD700]/60 text-[#FFD700]'
                                                : 'bg-[#0a0a0a] border-white/10 text-[#A0AAB2] hover:border-[#FFD700]/30'
                                        }`}
                                        data-testid={`freeplay-rollover-${opt.value}`}
                                    >
                                        {opt.value === 0 ? 'None' : `${opt.value}×`}
                                    </button>
                                );
                            })}
                        </div>
                        {rollover > 0 && parseFloat(amount || 0) > 0 && (
                            <p className="text-[11px] text-[#FFD700]/80 mt-1" data-testid="freeplay-rollover-summary">
                                Player must wager <span className="font-bold">${(parseFloat(amount) * rollover).toFixed(2)}</span> before cashing out.
                            </p>
                        )}
                    </div>

                    <DialogFooter className="pt-2">
                        <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-[#A0AAB2]">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting || !amount || parseFloat(amount) <= 0}
                            className="bg-[#FF007F] hover:bg-[#FF007F]/80 text-white font-bold"
                            data-testid="freeplay-submit"
                        >
                            {submitting ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                            ) : (
                                <><Gift className="w-4 h-4 mr-1" />Send Free Play</>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default FreePlayQuickDialog;
